import api from '../../../services/api';

export interface ApiResponse<T = any> {
    success: boolean;
    data: T;
    message?: string;
    meta?: {
        current_page: number;
        total_pages: number;
        total_count: number;
        per_page: number;
    };
}

/**
 * Accounting API Service
 * Centralized service for all accounting-related API calls
 */
export class AccountingApiService {
    // ============================================
    // ACCOUNT GROUPS
    // ============================================

    /**
     * Get all account groups
     * @param {Object} params - Query parameters for filtering
     * @returns {Promise} List of account groups
     */
    async getAccountGroups(params: object = {}): Promise<ApiResponse> {
        return api.get('/account_groups', params);
    }

    /**
     * Get a single account group by ID
     * @param {number} id - Account group ID
     * @returns {Promise} Account group data
     */
    async getAccountGroup(id: number | string): Promise<ApiResponse> {
        return api.get(`/account_groups/${id}`);
    }

    /**
     * Create a new account group
     * @param {Object} data - Account group data
     * @returns {Promise} Created account group
     */
    async createAccountGroup(data: object): Promise<ApiResponse> {
        return api.post('/account_groups', { account_group: data });
    }

    /**
     * Update an existing account group
     * @param {number} id - Account group ID
     * @param {Object} data - Updated account group data
     * @returns {Promise} Updated account group
     */
    async updateAccountGroup(id: number | string, data: object): Promise<ApiResponse> {
        return api.put(`/account_groups/${id}`, { account_group: data });
    }

    /**
     * Delete an account group
     * @param {number} id - Account group ID
     * @returns {Promise} Deletion confirmation
     */
    async deleteAccountGroup(id: number | string): Promise<ApiResponse> {
        return api.delete(`/account_groups/${id}`);
    }

    // ============================================
    // ACCOUNT HEADS
    // ============================================

    /**
     * Get all account heads
     * @returns {Promise} List of account heads
     */
    async getAccountHeads(): Promise<ApiResponse> {
        return api.get('/account_heads');
    }

    /**
     * Get a single account head by ID
     * @param {number} id - Account head ID
     * @returns {Promise} Account head data
     */
    async getAccountHead(id: number | string): Promise<ApiResponse> {
        return api.get(`/account_heads/${id}`);
    }

    /**
     * Create a new account head
     * @param {Object} data - Account head data
     * @returns {Promise} Created account head
     */
    async createAccountHead(data: object): Promise<ApiResponse> {
        return api.post('/account_heads', { account_head: data });
    }

    /**
     * Update an existing account head
     * @param {number} id - Account head ID
     * @param {Object} data - Updated account head data
     * @returns {Promise} Updated account head
     */
    async updateAccountHead(id: number | string, data: object): Promise<ApiResponse> {
        return api.put(`/account_heads/${id}`, { account_head: data });
    }

    /**
     * Delete an account head
     * @param {number} id - Account head ID
     * @returns {Promise} Deletion confirmation
     */
    async deleteAccountHead(id: number | string): Promise<ApiResponse> {
        return api.delete(`/account_heads/${id}`);
    }

    // ============================================
    // BANK ACCOUNTS
    // ============================================

    /**
     * Get all bank accounts
     * @returns {Promise} List of bank accounts
     */
    async getBankAccounts() {
        return api.get('/firm_bank_accounts');
    }

    /**
     * Get a single bank account by ID
     * @param {number} id - Bank account ID
     * @returns {Promise} Bank account data
     */
    async getBankAccount(id) {
        return api.get(`/firm_bank_accounts/${id}`);
    }

    /**
     * Create a new bank account
     * @param {Object} data - Bank account data
     * @returns {Promise} Created bank account
     */
    async createBankAccount(data) {
        return api.post('/firm_bank_accounts', { firm_bank_account: data });
    }

    /**
     * Update an existing bank account
     * @param {number} id - Bank account ID
     * @param {Object} data - Updated bank account data
     * @returns {Promise} Updated bank account
     */
    async updateBankAccount(id: number | string, data: object): Promise<ApiResponse> {
        return api.put(`/firm_bank_accounts/${id}`, { firm_bank_account: data });
    }

    /**
     * Delete a bank account
     * @param {number} id - Bank account ID
     * @returns {Promise} Deletion confirmation
     */
    async deleteBankAccount(id: number | string): Promise<ApiResponse> {
        return api.delete(`/firm_bank_accounts/${id}`);
    }

    // ============================================
    // VOUCHERS
    // ============================================

    /**
     * Get all vouchers with optional filters
     * @param {Object} params - Query parameters for filtering
     * @returns {Promise} List of vouchers
     */
    async getVouchers(params: object = {}): Promise<ApiResponse> {
        return api.get('/vouchers', params);
    }

    /**
     * Get a single voucher by ID
     * @param {number} id - Voucher ID
     * @returns {Promise} Voucher data with entries
     */
    async getVoucher(id: number | string): Promise<ApiResponse> {
        return api.get(`/vouchers/${id}`);
    }

    /**
     * Create a new voucher
     * @param {Object} data - Voucher data including entries
     * @returns {Promise} Created voucher
     */
    async createVoucher(data: object): Promise<ApiResponse> {
        return api.post('/vouchers', { voucher: data });
    }

    /**
     * Update an existing voucher
     * @param {number} id - Voucher ID
     * @param {Object} data - Updated voucher data
     * @returns {Promise} Updated voucher
     */
    async updateVoucher(id: number | string, data: object): Promise<ApiResponse> {
        return api.put(`/vouchers/${id}`, { voucher: data });
    }

    /**
     * Delete a voucher
     * @param {number} id - Voucher ID
     * @returns {Promise} Deletion confirmation
     */
    async deleteVoucher(id: number | string): Promise<ApiResponse> {
        return api.delete(`/vouchers/${id}`);
    }

    /**
     * Post a voucher (finalize it)
     * @param {number} id - Voucher ID
     * @returns {Promise} Posted voucher
     */
    async postVoucher(id: number | string): Promise<ApiResponse> {
        return api.post(`/vouchers/${id}/post`);
    }

    /**
     * Unpost a voucher (revert to draft)
     * @param {number} id - Voucher ID
     * @returns {Promise} Unposted voucher
     */
    async unpostVoucher(id: number | string): Promise<ApiResponse> {
        return api.post(`/vouchers/${id}/unpost`);
    }

    // ============================================
    // VOUCHER TYPES
    // ============================================

    /**
     * Get all voucher types
     * @returns {Promise} List of voucher types
     */
    async getVoucherTypes() {
        return api.get('/voucher_types');
    }

    /**
     * Get a single voucher type by ID
     * @param {number} id - Voucher type ID
     * @returns {Promise} Voucher type data
     */
    async getVoucherType(id) {
        return api.get(`/voucher_types/${id}`);
    }

    /**
     * Create a new voucher type
     * @param {Object} data - Voucher type data
     * @returns {Promise} Created voucher type
     */
    async createVoucherType(data: object): Promise<ApiResponse> {
        return api.post('/voucher_types', { voucher_type: data });
    }

    /**
     * Update an existing voucher type
     * @param {number} id - Voucher type ID
     * @param {Object} data - Updated voucher type data
     * @returns {Promise} Updated voucher type
     */
    async updateVoucherType(id: number | string, data: object): Promise<ApiResponse> {
        return api.put(`/voucher_types/${id}`, { voucher_type: data });
    }

    /**
     * Delete a voucher type
     * @param {number} id - Voucher type ID
     * @returns {Promise} Deletion confirmation
     */
    async deleteVoucherType(id: number | string): Promise<ApiResponse> {
        return api.delete(`/voucher_types/${id}`);
    }

    // ============================================
    // REPORTS & DASHBOARD
    // ============================================

    /**
     * Get accounting dashboard data
     * @returns {Promise} Dashboard statistics and summary
     */
    async getDashboard(): Promise<ApiResponse> {
        return api.get('/accounting/dashboard');
    }

    /**
     * Get day book report
     * @param {Object} params - Date range and filters
     * @returns {Promise} Day book entries
     */
    async getDayBook(params = {}) {
        return api.get('/accounting/reports/day_book', params);
    }

    /**
     * Get cash book report
     * @param {Object} params - Date range and filters
     * @returns {Promise} Cash book entries
     */
    async getCashBook(params = {}) {
        return api.get('/accounting/reports/cash_book', params);
    }

    /**
     * Get bank book report
     * @param {Object} params - Date range and filters
     * @returns {Promise} Bank book entries
     */
    async getBankBook(params = {}) {
        return api.get('/accounting/reports/bank_book', params);
    }

    /**
     * Get trial balance report
     * @param {Object} params - Date range and filters
     * @returns {Promise} Trial balance data
     */
    async getTrialBalance(params = {}) {
        return api.get('/accounting/reports/trial_balance', params);
    }

    /**
     * Get balance sheet report
     * @param {Object} params - Date and filters
     * @returns {Promise} Balance sheet data
     */
    async getBalanceSheet(params = {}) {
        return api.get('/accounting/reports/balance_sheet', params);
    }

    /**
     * Get profit & loss report
     * @param {Object} params - Date range and filters
     * @returns {Promise} Profit & loss data
     */
    async getProfitLoss(params = {}) {
        return api.get('/accounting/reports/profit_loss', params);
    }

    /**
     * Get income & expense report
     * @param {Object} params - Date range and filters
     * @returns {Promise} Income & expense data
     */
    async getIncomeExpense(params: object = {}): Promise<ApiResponse> {
        return api.get('/accounting/reports/income_expense', params);
    }

    /**
     * Get ledger statement for an account
     * @param {Object} params - Account ID, date range, and filters
     * @returns {Promise} Ledger statement entries
     */
    async getLedgerStatement(params: object = {}): Promise<ApiResponse> {
        return api.get('/accounting/reports/ledger_statement', params);
    }
}

export default new AccountingApiService();
